<template>
  <o-dialog
    ref="dialog"
    :title="$t('general.image')"
    :info="$t('components.pass.actions.image.info', { name: pass.name })"
    :options="[
      { text: $t('general.cancel'), type: 'cancel'},
      { text: $t('asset.deleteConfirm2'), type: 'confirm', variant: 'danger', callback: handleDelete, validate: false },
      { text: $t('asset.upload'), type: 'confirm', callback: handleConfirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="mt-4 text-left ">
      <o-form v-if="!loading" ref="form">
        <o-field-group :label="$t('general.image')" :name="$t('general.image')" class="block pb-3">
          <o-upload v-model="image" />
        </o-field-group>
      </o-form>
    </div>

    <template #loading>
      <div v-if="loading" class="w-full max-w-sm mx-auto">
        <o-progress v-if="progress" :progress="progress" :label="$t('general.uploading')" />
        <o-loader v-else class="mx-auto" />
      </div>
    </template>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  OUpload,
  OProgress,
} from '@/components/elements'

interface Props {
  pass: any
  portal?: string
}

interface Emits {
  (e: 'success'): void
  (e: 'update', pass: any): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const image = ref<File | null>(null)
const loading = ref(false)
const progress = ref(0)

const upload = computed({
  get: () => {
    if (image.value instanceof File) {
      return image.value
    }
    return null
  },
  set: (value) => {
    image.value = value
  },
})

const open = () => {
  image.value = null
  dialog.value.open()
}

const validate = () => {
  return !!image.value
}

const handleOnUploadProgress = (event: ProgressEvent) => {
  progress.value = Math.round((event.loaded / event.total) * 100)
}

const handleConfirm = async () => {
  loading.value = true

  try {
    const imageFile = upload.value
    const options = { onUploadProgress: (event: ProgressEvent) => handleOnUploadProgress(event) }

    const response = await $api.pass.addPhoto(props.pass, imageFile!, options)
    const pass = response.data

    if (pass) {
      $notification.toast({
        title: $i18n.t('asset.addedPhoto'),
        text: $i18n.t('asset.successInfo2', { label: props.pass.name }),
        variant: 'success',
      })

      emit('success')
      emit('update', pass)

      loading.value = false
      progress.value = 0

      return true
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const handleDelete = async () => {
  loading.value = true

  try {
    const deleteResponse = await $api.pass.deletePhoto(props.pass)
    const pass = deleteResponse.data

    if (pass) {
      $notification.toast({
        title: $i18n.t('asset.deleteSuccess'),
        text: $i18n.t('asset.deleteSuccessInfo2', { label: props.pass.name }),
        variant: 'success',
      })

      emit('success')
      emit('update', pass)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

defineExpose({ open })
</script>
